import { Component, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { MasterService, MasterItem } from '../../services/master.service';
import { ClinicSelectionService } from '../../services/clinic-selection.service';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { Subject, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, tap, switchMap, catchError, map, takeUntil, filter } from 'rxjs/operators';
import { AlertService } from '../../services/alert.service';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, RouterModule, NgbDropdownModule, FormsModule, NgSelectModule],
  templateUrl: './header.html',
  styleUrls: ['./header.scss']
})
export class Header implements OnInit, OnDestroy {
  @Output() toggleSidebar = new EventEmitter<void>();
  currentUserEmail: string = '';
  currentUserName: string = '';
  showUserMenu = false;
  notifications = [
    {
      user: 'Dona williams',
      message: 'has successfully created claim draft for the patient',
      patient: 'Mark Johnson',
      time: '9:35 AM 11/28/2025',
      icon: 'note_alt',
      type: 'orange'
    },
    {
      user: 'Dona williams',
      message: 'has successfully uploaded document for the patient',
      patient: 'Mark Johnson',
      time: '9:35 AM 11/28/2025',
      icon: 'upload_file',
      type: 'purple'
    },
    {
      user: 'Dona williams',
      message: 'has reviewed and edited claim CLM-2020 for the patient',
      patient: 'Mark Johnson',
      time: '9:35 AM 11/28/2025',
      icon: 'visibility',
      type: 'blue'
    },
    {
      user: 'Dona williams',
      message: 'has finalized the claim CLM-2025/004 for the patient',
      patient: 'Mark Johnson',
      time: '9:35 AM 11/28/2025',
      icon: 'task_alt',
      type: 'green'
    },
    {
      user: 'Dona williams',
      message: 'has finalized the claim CLM-2025/004 for the patient',
      patient: 'Mark Johnson',
      time: '9:35 AM 11/28/2025',
      icon: 'task_alt',
      type: 'green'
    },
    {
      user: 'Dona williams',
      message: 'has finalized the claim CLM-2025/004 for the patient',
      patient: 'Mark Johnson',
      time: '9:35 AM 11/28/2025',
      icon: 'task_alt',
      type: 'green'
    },
    {
      user: 'Dona williams',
      message: 'has finalized the claim CLM-2025/004 for the patient',
      patient: 'Mark Johnson',
      time: '9:35 AM 11/28/2025',
      icon: 'task_alt',
      type: 'green'
    },
    {
      user: 'Dona williams',
      message: 'has finalized the claim CLM-2025/004 for the patient',
      patient: 'Mark Johnson',
      time: '9:35 AM 11/28/2025',
      icon: 'task_alt',
      type: 'green'
    },
    {
      user: 'Dona williams',
      message: 'has finalized the claim CLM-2025/004 for the patient',
      patient: 'Mark Johnson',
      time: '9:35 AM 11/28/2025',
      icon: 'task_alt',
      type: 'green'
    },
    {
      user: 'Dona williams',
      message: 'has finalized the claim CLM-2025/004 for the patient',
      patient: 'Mark Johnson',
      time: '9:35 AM 11/28/2025',
      icon: 'task_alt',
      type: 'green'
    }
  ];


  clinics: MasterItem[] = [];
  selectedClinicId: number | null = null;

  isClinicLoading = false;
  showClinicDropdown = false;
  private destroy$ = new Subject<void>();

  constructor(
    private authService: AuthService,
    private router: Router,
    private masterService: MasterService,
    private clinicSelectionService: ClinicSelectionService,
    private alertService: AlertService
  ) {
    const user = this.authService.getCurrentUser();
    if (user) {
      this.currentUserEmail = user.email;
      this.currentUserName = user.fullname || user.email;
    }

    this.router.events.pipe(
      filter((event: any) => event instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe((event: any) => {
      this.checkRoute(event.urlAfterRedirects);
    });
  }

  ngOnInit(): void {
    // Check initial route
    this.checkRoute(this.router.url);

    // Subscribe to selection changes to keep local state in sync
    this.clinicSelectionService.selectedClinicId$.pipe(takeUntil(this.destroy$)).subscribe(id => {
      this.selectedClinicId = id;
      const name = this.clinicSelectionService.getClinicName();
      if (id && name) {
        if (!this.clinics.find(c => Number(c.id) === Number(id))) {
          this.clinics = [{ id: Number(id), value: name, type: 'Clinic' }, ...this.clinics];
        }
      }
    });

    this.loadAllClinics();
  }

  checkRoute(url: string): void {
    const activeRoutes = [
      '/patients',
      '/claim-form-rules',
      '/ocr-settings',
      '/claims',
      '/documents',
      '/reports'
    ];
    this.showClinicDropdown = activeRoutes.some(route => url.startsWith(route));
    
    if (this.showClinicDropdown && !this.selectedClinicId && this.clinics.length > 0) {
      this.alertService.error('Please select a clinic to view this module.');
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadAllClinics(): void {
    this.isClinicLoading = true;
    this.masterService.getMasterData('Clinic', { FilterType: 'Clinic' })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isClinicLoading = false;
          if (response.status && response.data) {
            // Force id to be Number so ng-select strict equality works when selectedClinicId is a Number
            this.clinics = response.data.map((c: any) => ({
              ...c,
              id: Number(c.id)
            }));
            
            if (!this.selectedClinicId && this.clinics.length > 0) {
              const firstClinic = this.clinics[0];
              this.onClinicChange(firstClinic);
            } else if (this.showClinicDropdown && !this.selectedClinicId) {
              this.alertService.error('Please select a clinic to view this module.');
            }
          }
        },
        error: () => {
          this.isClinicLoading = false;
        }
      });
  }



  onClinicChange(event: any): void {
    // NgSelect emits undefined when cleared
    const clinicId = event?.id ? Number(event.id) : null;
    const clinicName = event?.value || null;

    if (clinicId && clinicName) {
      if (!this.clinics.find((c: any) => Number(c.id) === clinicId)) {
        this.clinics = [{ id: clinicId, value: clinicName, type: 'Clinic' }, ...this.clinics];
      }
    }

    this.clinicSelectionService.setClinicId(clinicId, clinicName);
    
    if (!clinicId && this.showClinicDropdown) {
      this.alertService.error('Please select a clinic to view this module.');
    }

    // Redirect to listing page if we are in a details/add/edit screen of a clinic-specific module
    const currentPath = this.router.url.split('?')[0];
    const clinicSpecificModules = [
      '/patients',
      '/claim-form-rules',
      '/ocr-settings',
      '/claims',
      '/documents',
      '/codes',
      '/reports/claims',
      '/reports/financial',
      '/reports/split-claims',
      '/reports/token-reports'
    ];

    for (const moduleRoute of clinicSpecificModules) {
      if (currentPath.startsWith(moduleRoute + '/')) {
        this.router.navigate([moduleRoute]);
        break;
      }
    }
  }

  toggleUserMenu(): void {
    this.showUserMenu = !this.showUserMenu;
  }

  onToggleSidebar(): void {
    this.toggleSidebar.emit();
  }

  profile(): void {
    this.router.navigate(['/profile']);
  }

  logout(): void {
    this.authService.logout().subscribe({
      next: () => {
        this.router.navigate(['/login']);
      },
      error: () => {
        // Even if logout API fails, redirect to login
        this.router.navigate(['/login']);
      }
    });
  }

  getInitials(): string {
    const user = this.authService.getCurrentUser();
    if (user && user.fullname) {
      const names = user.fullname.split(' ');
      if (names.length >= 2) {
        return (names[0][0] + names[names.length - 1][0]).toUpperCase();
      }
      return user.fullname.substring(0, 2).toUpperCase();
    }
    return user?.email?.substring(0, 2).toUpperCase() || 'U';
  }

  getUserRole(): string {
    const user = this.authService.getCurrentUser();
    return user?.role || 'User';
  }
}
